
import { useState } from "react";
import { useAuthForm } from "@/context/auth-form";
import { useAuthHandler } from "@/hooks/useAuthHandler";
import { useAuth } from "@/context/auth";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { getErrorMessage } from "@/hooks/auth/authErrorHandler";

export const usePasswordReset = () => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const { handleUpdatePassword } = useAuthHandler();
  const { forceSignOut } = useAuth();
  const navigate = useNavigate();

  const resetPassword = async (password: string, confirmPassword: string) => {
    setErrorMessage("");
    setLoading(true);
    
    try {
      if (!password || !confirmPassword) {
        setErrorMessage("Please enter and confirm your new password");
        setLoading(false);
        return;
      }
      
      if (password !== confirmPassword) {
        setErrorMessage("Passwords don't match");
        setLoading(false);
        return;
      }
      
      // Verify we have a valid session before updating password
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        console.error("Session check failed:", sessionError);
        setErrorMessage("Your password reset link is invalid or has expired.");
        
        // Mark as expired in session storage and redirect
        sessionStorage.setItem('token_expired', 'true');
        navigate("/auth?mode=forgotPassword&token_expired=true", { replace: true });
        
        setLoading(false);
        return;
      }
      
      const result = await handleUpdatePassword(password, confirmPassword);
      
      if (result.success) {
        console.log("Password updated successfully");
        
        // Sign out immediately
        await supabase.auth.signOut();
        await forceSignOut();
        
        // Show success screen
        setPasswordUpdated(true);
      } else {
        // Map error messages
        const errorMsg = getErrorMessage(result.error || new Error("Failed to reset password"));
        setErrorMessage(errorMsg);
      }
    } catch (error: any) {
      console.error("Reset password error:", error);
      const errorMsg = getErrorMessage(error);
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    errorMessage,
    passwordUpdated,
    resetPassword
  };
};
